import { logger } from './logger';

export interface RateLimiterOptions {
  maxRequests: number;    // Maximum requests allowed
  windowMs: number;       // Time window in milliseconds
  name: string;          // Name for logging
}

/**
 * Rate Limiter implementation
 * Prevents abuse by limiting number of requests in a time window
 * Persists state in localStorage to prevent F5 bypass
 *
 * @example
 * const limiter = new RateLimiter({ name: 'ResaleCheckout', maxRequests: 3, windowMs: 60000 });
 * if (!limiter.canProceed()) {
 *   throw new Error('Too many requests, please wait');
 * }
 */
export class RateLimiter {
  private timestamps: number[] = [];
  private readonly storageKey: string;

  private readonly maxRequests: number;
  private readonly windowMs: number;
  private readonly name: string;

  constructor(options: RateLimiterOptions) {
    this.maxRequests = options.maxRequests;
    this.windowMs = options.windowMs;
    this.name = options.name;
    this.storageKey = `rate_limiter_${options.name}`;

    // Load persisted timestamps from localStorage
    this.loadFromStorage();
  }

  /**
   * Load timestamps from localStorage
   * Filters out expired timestamps
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as number[];
        const now = Date.now();

        // Only keep timestamps within the window
        this.timestamps = parsed.filter(timestamp => now - timestamp < this.windowMs);

        logger.info(
          `[RateLimiter:${this.name}] Loaded ${this.timestamps.length} timestamps from storage`
        );
      }
    } catch (error) {
      logger.error(`[RateLimiter:${this.name}] Failed to load from storage`, { error });
      this.timestamps = [];
    }
  }

  /**
   * Save timestamps to localStorage
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.timestamps));
    } catch (error) {
      logger.error(`[RateLimiter:${this.name}] Failed to save to storage`, { error });
    }
  }

  /**
   * Check if a new request can proceed
   * Cleans up old timestamps and adds new one if allowed
   */
  canProceed(): boolean {
    const now = Date.now();

    // Remove timestamps outside the window
    this.timestamps = this.timestamps.filter(timestamp => now - timestamp < this.windowMs);

    // Check if limit reached
    if (this.timestamps.length >= this.maxRequests) {
      const oldestTimestamp = this.timestamps[0];
      const timeUntilReset = Math.ceil((this.windowMs - (now - oldestTimestamp)) / 1000);

      logger.warn(
        `[RateLimiter:${this.name}] Rate limit exceeded. ` +
        `${this.timestamps.length}/${this.maxRequests} requests in window. ` +
        `Retry in ${timeUntilReset}s`
      );

      return false;
    }

    // Add current timestamp and allow request
    this.timestamps.push(now);
    logger.info(`[RateLimiter:${this.name}] Request allowed. ${this.timestamps.length}/${this.maxRequests} in window`);

    // Persist to localStorage to prevent F5 bypass
    this.saveToStorage();

    return true;
  }

  /**
   * Get remaining requests in current window
   */
  getRemainingRequests(): number {
    const now = Date.now();
    this.timestamps = this.timestamps.filter(timestamp => now - timestamp < this.windowMs);
    return Math.max(0, this.maxRequests - this.timestamps.length);
  }

  /**
   * Get time until rate limit resets (in ms)
   */
  getTimeUntilReset(): number {
    if (this.timestamps.length === 0) return 0;

    const now = Date.now();
    const oldestTimestamp = this.timestamps[0];
    return Math.max(0, this.windowMs - (now - oldestTimestamp));
  }

  /**
   * Manually reset the rate limiter
   */
  reset(): void {
    this.timestamps = [];
    this.saveToStorage(); // Clear from localStorage
    logger.info(`[RateLimiter:${this.name}] Manually reset`);
  }
}
